
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  BarChart, 
  Users, 
  Target, 
  Building, 
  Cpu, 
  Package, 
  Scale,
  FileCheck,
  Camera,
  Shield,
  MessageCircle,
  AlertTriangle,
  Receipt
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDocumentCreation } from "@/components/documents/use-document-creation";
import { usePersona } from "@/hooks/use-persona";
import { useToast } from "@/hooks/use-toast";

export const PersonaQuickActions = () => {
  const navigate = useNavigate();
  const { createDocument, isCreating } = useDocumentCreation();
  const { currentPersona } = usePersona();
  const { toast } = useToast();

  const csoActions = [
    {
      id: "business_plan",
      title: "Business Plan",
      description: "Create a comprehensive business plan",
      icon: FileText,
      color: "bg-blue-500",
    },
    {
      id: "strategic_plan",
      title: "Strategic Plan",
      description: "Develop strategic roadmap",
      icon: Target,
      color: "bg-orange-500",
    },
  ];

  const cfoActions = [
    {
      id: "financial_forecast",
      title: "Financial Forecast",
      description: "Build financial projections",
      icon: BarChart,
      color: "bg-green-500",
    },
    {
      id: "budget_template",
      title: "Budget Template",
      description: "Create budget templates",
      icon: FileText,
      color: "bg-blue-500",
    },
  ];

  const ctoActions = [
    {
      id: "tech_architecture",
      title: "Tech Architecture",
      description: "Design system architecture",
      icon: Cpu,
      color: "bg-purple-500",
    },
    {
      id: "digital_transformation_plan",
      title: "Digital Transformation",
      description: "Plan digital transformation",
      icon: Building,
      color: "bg-indigo-500",
    },
  ];

  const cooActions = [
    {
      id: "operations_manual",
      title: "Operations Manual",
      description: "Create operations documentation",
      icon: FileCheck,
      color: "bg-orange-500",
    },
    {
      id: "process_optimization",
      title: "Process Optimization",
      description: "Optimize business processes",
      icon: Target,
      color: "bg-red-500",
    },
  ];

  const cpoActions = [
    {
      id: "product_roadmap",
      title: "Product Roadmap",
      description: "Plan product development",
      icon: Package,
      color: "bg-green-500",
    },
    {
      id: "user_research_plan",
      title: "User Research Plan",
      description: "Plan user research activities",
      icon: Users,
      color: "bg-blue-500",
    },
  ];

  // Templates section with all contract documents and business templates
  const templateActions = [
    {
      id: "consulting_agreement",
      title: "Consulting Agreement",
      description: "Professional service contracts",
      icon: Scale,
      color: "bg-indigo-500",
    },
    {
      id: "service_contract",
      title: "Service Contract",
      description: "General service agreements",
      icon: FileCheck,
      color: "bg-green-500",
    },
    {
      id: "media_release",
      title: "Media Release",
      description: "Media usage agreements",
      icon: Camera,
      color: "bg-purple-500",
    },
    {
      id: "nda_agreement",
      title: "NDA Agreement",
      description: "Non-disclosure agreements",
      icon: Shield,
      color: "bg-red-500",
    },
    {
      id: "invoice_generator",
      title: "Invoice Generator",
      description: "Professional invoice creation",
      icon: Receipt,
      color: "bg-blue-500",
    },
    {
      id: "proposal_generator",
      title: "Proposal Generator",
      description: "Professional project proposals",
      icon: FileText,
      color: "bg-green-500",
    },
    {
      id: "risk_mitigation_plan",
      title: "Risk Mitigation Plan",
      description: "Comprehensive risk assessment",
      icon: AlertTriangle,
      color: "bg-orange-500",
    },
  ];

  const getActionsForPersona = () => {
    switch (currentPersona) {
      case 'CSO':
        return csoActions;
      case 'CFO':
        return cfoActions;
      case 'CTO':
        return ctoActions;
      case 'COO':
        return cooActions;
      case 'CPO':
        return cpoActions;
      default:
        return csoActions;
    }
  };

  const getPersonaTitle = () => {
    switch (currentPersona) {
      case 'CSO':
        return 'CSO Quick Actions';
      case 'CFO':
        return 'CFO Quick Actions';
      case 'CTO':
        return 'CTO Quick Actions';
      case 'COO':
        return 'COO Quick Actions';
      case 'CPO':
        return 'CPO Quick Actions';
      default:
        return 'Quick Actions';
    }
  };

  const handleQuickAction = async (actionId: string) => {
    try {
      console.log('Creating document with actionId:', actionId);
      
      const allActions = [...csoActions, ...cfoActions, ...ctoActions, ...cooActions, ...cpoActions, ...templateActions];
      const action = allActions.find(a => a.id === actionId);
      const title = action ? action.title : actionId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      console.log('Found action:', action, 'Title:', title);
      
      await createDocument(actionId as any, title);
      
      toast({
        title: "Document Created",
        description: `${title} has been created successfully`,
      });
    } catch (error) {
      console.error('Error creating document:', error);
      toast({
        title: "Error",
        description: "Failed to create document. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleChatNavigation = () => {
    navigate('/chat');
  };

  const actions = getActionsForPersona();

  return (
    <>
      {/* CSO Chat Button */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            CSO Chat & Voice Assistant
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleChatNavigation}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Start Chat with Your AI CSO Team
          </Button>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            {getPersonaTitle()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <Button
                  key={action.id}
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start gap-2 text-left hover:bg-gray-50"
                  onClick={() => handleQuickAction(action.id)}
                  disabled={isCreating}
                >
                  <div className={`p-2 rounded-lg ${action.color} text-white`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{action.title}</div>
                    <div className="text-xs text-gray-500">{action.description}</div>
                  </div>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Templates Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Templates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {templateActions.map((action) => {
              const Icon = action.icon;
              return (
                <Button
                  key={action.id}
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start gap-2 text-left hover:bg-gray-50"
                  onClick={() => handleQuickAction(action.id)}
                  disabled={isCreating}
                >
                  <div className={`p-2 rounded-lg ${action.color} text-white`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{action.title}</div>
                    <div className="text-xs text-gray-500">{action.description}</div>
                  </div>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
};
